// src/components/LevelEditor.js
import React, { useState, useEffect, useRef } from 'react';
import CellEditorPopup from './CellEditorPopup';
import './LevelEditor.css';

// Define our available tools (this part is unchanged)
const TOOLS = {
  SET_NUMBER: { type: 'number' },
  SET_HINT: { type: 'hint' },
  ERASER: { type: 'eraser' },
  BORDER_RIGHT: { type: 'border', value: 'right' },
  BORDER_BOTTOM: { type: 'border', value: 'bottom' },
  SOLUTION_PATH: { type: 'path' },
};

const GRID_SIZE_PRESETS = [6, 7, 8];

const LevelEditor = ({ initialData, onSave, onCancel, getNextLevel }) => {
  const [puzzle, setPuzzle] = useState(() => JSON.parse(JSON.stringify(initialData)));
  const [activeTool, setActiveTool] = useState(TOOLS.SET_NUMBER);
  const [editingCell, setEditingCell] = useState(null);
  const [sendNotification, setSendNotification] = useState(true);
  // Trạng thái đang giữ+kéo để vẽ path không cần render lại UI, dùng ref để tránh stale closure
  // trong các listener touch gắn thủ công (ngoài hệ thống event delegation của React).
  const isDrawingPathRef = useRef(false);
  const activeToolRef = useRef(activeTool);
  const lastTouchCellRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  // End path drawing even if the pointer is released outside the grid
  useEffect(() => {
    const stopDrawing = () => {
      isDrawingPathRef.current = false;
      lastTouchCellRef.current = null;
    };
    window.addEventListener('mouseup', stopDrawing);
    window.addEventListener('touchend', stopDrawing);
    window.addEventListener('touchcancel', stopDrawing);
    return () => {
      window.removeEventListener('mouseup', stopDrawing);
      window.removeEventListener('touchend', stopDrawing);
      window.removeEventListener('touchcancel', stopDrawing);
    };
  }, []);

  // Touch không phát sinh mouseenter cho từng ô khi kéo ngón tay, nên phải tự dò ô đang chạm
  // qua elementFromPoint. Gắn thủ công (không qua JSX) với { passive: false } để preventDefault
  // được, ngăn trang cuộn theo khi đang vẽ path bằng ngón tay.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const handleTouchMove = (e) => {
      if (activeToolRef.current.type !== 'path' || !isDrawingPathRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      const cellEl = target && target.closest('.grid-cell');
      if (!cellEl) return;

      e.preventDefault();
      const r = parseInt(cellEl.dataset.row, 10);
      const c = parseInt(cellEl.dataset.col, 10);
      const last = lastTouchCellRef.current;
      if (last && last.r === r && last.c === c) return;
      lastTouchCellRef.current = { r, c };
      applyPathCell(r, c);
    };

    grid.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => grid.removeEventListener('touchmove', handleTouchMove);
  }, []);

  const generateRandomId = () => {
    // A simple combination of a prefix and timestamp for uniqueness
    const newId = `p_${Date.now().toString(36)}`;
    setPuzzle(prev => ({ ...prev, puzzleId: newId }));
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = name === 'level' || name === 'width' || name === 'height'
      ? parseInt(value, 10) || 0 // Use 0 if parsing fails
      : value;

    setPuzzle(prev => {
      const newPuzzle = { ...prev, [name]: parsedValue };

      // If width or height is changed, immediately regenerate the grid
      if (name === 'width' || name === 'height') {
        newPuzzle.flatGrid = Array.from(
          { length: newPuzzle.width * newPuzzle.height },
          () => ({ n: 0, b: [] })
        );
        newPuzzle.solutionPath = []; // Also reset the solution path
      }

      // If difficulty changed, auto-update level if getNextLevel is provided
      if (name === 'difficulty' && getNextLevel) {
        newPuzzle.level = getNextLevel(parsedValue);
      }

      return newPuzzle;
    });
  };

  const handleGridSizePreset = (size) => {
    setPuzzle(prev => ({
      ...prev,
      width: size,
      height: size,
      flatGrid: Array.from({ length: size * size }, () => ({ n: 0, b: [] })),
      solutionPath: [],
    }));
  };

  const handleCellClick = (r, c) => {
    const newPuzzle = JSON.parse(JSON.stringify(puzzle));
    const cellIndex = r * newPuzzle.width + c;

    // "Set Hint" vẫn mở pop-up để chọn các cạnh
    if (activeTool.type === 'hint') {
      const cellData = { ...newPuzzle.flatGrid[cellIndex], r, c };
      setEditingCell(cellData);
      return;
    }

    // "Set Number": click ô trống -> tự điền số tăng dần; click ô đã có số -> xóa số đó
    if (activeTool.type === 'number') {
      const cell = newPuzzle.flatGrid[cellIndex];
      if (cell.n > 0) {
        cell.n = 0;
      } else {
        const maxNumber = newPuzzle.flatGrid.reduce((max, c) => Math.max(max, c.n), 0);
        cell.n = maxNumber + 1;
      }
    }
    // Logic cho các tool khác (path được xử lý riêng qua mousedown/mouseenter để hỗ trợ kéo chuột)
    else if (activeTool.type === 'border') {
      const cell = newPuzzle.flatGrid[cellIndex];
      const borderIndex = cell.b.indexOf(activeTool.value);
      if (borderIndex > -1) cell.b.splice(borderIndex, 1);
      else cell.b.push(activeTool.value);
    }
    else if (activeTool.type === 'eraser') {
      const cell = newPuzzle.flatGrid[cellIndex];
      cell.n = 0;
      cell.b = [];
    }
    setPuzzle(newPuzzle);
  };

  // Áp dụng một ô vào solution path. Dùng chung cho click đơn và kéo chuột (hold + drag).
  const applyPathCell = (r, c) => {
    setPuzzle(prev => {
      const newPuzzle = JSON.parse(JSON.stringify(prev));
      const pathIndex = newPuzzle.solutionPath.findIndex(p => p.row === r && p.col === c);
      const lastCell = newPuzzle.solutionPath[newPuzzle.solutionPath.length - 1];

      if (pathIndex > -1) {
        // Click/kéo lại vào ô đã có trong path -> lùi path về đúng điểm đó (hỗ trợ "quay lại" khi kéo chuột)
        newPuzzle.solutionPath.splice(pathIndex);
      } else if (!lastCell || (Math.abs(lastCell.row - r) + Math.abs(lastCell.col - c) === 1)) {
        // Chỉ nối thêm ô nếu nó liền kề ô cuối cùng, tránh path bị "nhảy cóc" khi kéo chuột nhanh
        newPuzzle.solutionPath.push({ row: r, col: c });
      }
      return newPuzzle;
    });
  };

  const handlePathDrawStart = (r, c) => {
    isDrawingPathRef.current = true;
    lastTouchCellRef.current = { r, c };
    applyPathCell(r, c);
  };

  const handlePathMouseEnter = (r, c) => {
    if (isDrawingPathRef.current) applyPathCell(r, c);
  };

  const handleCellUpdate = (updatedData) => {
    const newPuzzle = JSON.parse(JSON.stringify(puzzle));

    if (updatedData.type === 'hint') {
      const { r, c, borders } = updatedData;
      const hintIndex = newPuzzle.hints.findIndex(h => h.row === r && h.col === c);
      if (hintIndex > -1) {
        if (borders.length > 0) newPuzzle.hints[hintIndex].borders = borders;
        else newPuzzle.hints.splice(hintIndex, 1);
      } else if (borders.length > 0) {
        newPuzzle.hints.push({ row: r, col: c, borders });
      }
    }
    setPuzzle(newPuzzle);
  };

  const deleteHint = (indexToDelete) => {
    const newPuzzle = JSON.parse(JSON.stringify(puzzle));
    newPuzzle.hints.splice(indexToDelete, 1);
    setPuzzle(newPuzzle);
  };

  const renderGrid = () => {
    if (!puzzle.flatGrid || puzzle.width === 0 || puzzle.height === 0) return null;

    const cells = [];
    for (let r = 0; r < puzzle.height; r++) {
      for (let c = 0; c < puzzle.width; c++) {
        const cellIndex = r * puzzle.width + c;
        const cell = puzzle.flatGrid[cellIndex];
        const pathIndex = puzzle.solutionPath.findIndex(p => p.row === r && p.col === c);

        const cellClasses = `grid-cell ${cell.b.join(' ')}`;
        const isPathTool = activeTool.type === 'path';
        cells.push(
          <div
            key={`${r}-${c}`}
            className={cellClasses}
            data-row={r}
            data-col={c}
            onClick={() => { if (!isPathTool) handleCellClick(r, c); }}
            onMouseDown={() => { if (isPathTool) handlePathDrawStart(r, c); }}
            onMouseEnter={() => { if (isPathTool) handlePathMouseEnter(r, c); }}
            onTouchStart={() => { if (isPathTool) handlePathDrawStart(r, c); }}
          >
            {cell.n > 0 && <span className="grid-cell-number">{cell.n}</span>}
            {pathIndex > -1 && <div className="path-indicator">{pathIndex + 1}</div>}
          </div>
        );
      }
    }
    return cells;
  };

  const handleSave = async () => {
    const updatedPuzzle = {
      ...puzzle,
      sendNotification: sendNotification, // Thêm trường này để Cloud Function v2 kích hoạt
      updatedAt: new Date()
    };

    console.log(updatedPuzzle);
    // Gọi hàm onSave hiện tại của bạn
    onSave(updatedPuzzle);
  };

  if (!puzzle.flatGrid) {
    return <div>Loading editor...</div>
  }

  return (
    <div className="level-editor">
      {editingCell && (
        <CellEditorPopup
          cell={editingCell}
          onUpdate={handleCellUpdate}
          onClose={() => setEditingCell(null)}
        />
      )}
      <h3>{initialData.id ? `Editing: ${initialData.id}` : 'Create New Level'}</h3>
      <div className="meta-form">
        <div className="form-group">
          <label>Puzzle ID</label>
          <div className="input-with-button">
            <input name="puzzleId" value={puzzle.puzzleId} onChange={handleMetaChange} placeholder="Puzzle ID" />
            <button onClick={generateRandomId}>Generate</button>
          </div>
        </div>
        <div className="form-group">
          <label>Difficulty</label>
          <select name="difficulty" value={puzzle.difficulty} onChange={handleMetaChange}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
        <div className="form-group">
          <label>Level</label>
          <input name="level" type="number" value={puzzle.level} onChange={handleMetaChange} />
        </div>
      </div>
      <div className="meta-form">
        <div className="form-group">
          <label>Width</label>
          <input name="width" type="number" value={puzzle.width} onChange={handleMetaChange} />
        </div>
        <div className="form-group">
          <label>Height</label>
          <input name="height" type="number" value={puzzle.height} onChange={handleMetaChange} />
        </div>
        <div className="form-group">
          <label>Grid Size Preset</label>
          <div className="preset-buttons">
            {GRID_SIZE_PRESETS.map(size => (
              <button
                key={size}
                type="button"
                onClick={() => handleGridSizePreset(size)}
                className={puzzle.width === size && puzzle.height === size ? 'preset-button active' : 'preset-button'}
              >
                {size}x{size}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="tools-container">
        {Object.entries(TOOLS).map(([key, tool]) => (
          <button
            key={key}
            onClick={() => setActiveTool(tool)}
            className={(activeTool.type === tool.type && activeTool.value === tool.value) ? 'tool-button active' : 'tool-button'}
          >
            {key.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
      {activeTool.type === 'path' && (
        <p className="tool-hint">Giữ chuột (hoặc giữ ngón tay trên mobile) và kéo qua các ô liền kề để vẽ Solution Path. Kéo lại vào một ô đã đi qua để lùi path về điểm đó.</p>
      )}
      {activeTool.type === 'number' && (
        <p className="tool-hint">Click ô trống để tự động điền số tăng dần. Click ô đã có số để xóa số đó.</p>
      )}

      <div className="notification-control" style={{
        margin: '20px 0',
        padding: '10px',
        background: '#e3f2fd',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <input
          type="checkbox"
          id="sendNotify"
          checked={sendNotification}
          onChange={(e) => setSendNotification(e.target.checked)}
          style={{ width: '20px', height: '20px', cursor: 'pointer' }}
        />
        <label htmlFor="sendNotify" style={{ cursor: 'pointer', fontWeight: 'bold', color: '#1976d2' }}>
          Gửi thông báo "Level mới" cho người chơi sau khi lưu
        </label>
      </div>

      <div
        ref={gridRef}
        className="grid-container"
        style={{
          gridTemplateColumns: `repeat(${puzzle.width}, 1fr)`,
          maxWidth: `${puzzle.width * 60}px`,
        }}
      >
        {renderGrid()}
      </div>

      <div className="hints-list">
        <h4>Defined Hints (in order)</h4>
        {puzzle.hints && puzzle.hints.map((hint, index) => (
          <div key={index} className="hint-item">
            <span>{`Step ${index + 1}: Cell [${hint.row}, ${hint.col}] -> Borders: [${hint.borders.join(', ')}]`}</span>
            <button onClick={() => deleteHint(index)} className="delete-hint-btn">X</button>
          </div>
        ))}
        {(!puzzle.hints || puzzle.hints.length === 0) && <p>No hints defined yet.</p>}
      </div>

      <div className="editor-actions">
        {/* Cập nhật nút Save để gọi handleSave thay vì gọi trực tiếp onSave(puzzle) */}
        <button className="crud-button save" onClick={handleSave}>Save</button>
        <button className="crud-button cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default LevelEditor;